import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { InvestmentService } from '../investment.service';
import { InvestmentsDepositsModel } from '../investment.model';
import { consts } from '../../../consts';
import { Utils } from '../../../shared/utils';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import {
  InvestmentAssetOption,
  InvestmentAssetLookupsFacade,
  InvestmentAssetPayKind,
} from '../../../modules_v2/investment/investment-asset-shared/services/investment-asset-lookups.facade';
import {
  InvestmentAssetDeposit,
  InvestmentAssetPayTarget,
  roundMoney,
} from '../../../modules_v2/investment/investment-asset-shared/models/investment-asset.model';

export interface InvestmentDialogPayData {
  investment: InvestmentAssetPayTarget;
  editUid?: number;
}

const KIND_CONTROLS: Record<InvestmentAssetPayKind, string> = {
  bankAccounts: 'bankAccount',
  paymentCards: 'paymentCard',
  cashRegisters: 'cashRegister',
  partners: 'partner',
  creditPolicies: 'creditPolicy',
};

@Component({
    selector: 'convfact-investmentpay',
    templateUrl: './investment-dialog-pay.component.html',
    styleUrls: ['./investment-dialog-pay.component.scss'],
    standalone: false
})
export class InvestmentDialogPayComponent implements OnInit {

  readonly methods = {
    cash: consts.TYPEPAY_CASH,
    creditCard: consts.TYPEPAY_CREDITCARD_PAY,
    transfer: consts.TYPEPAY_BANKTRANSFERENCE,
    partner: consts.TYPE_SOCIO,
    creditPolicy: consts.TYPE_POLIZACREDITO,
  };

  readonly methodOptions = [
    consts.TYPEPAY_CASH,
    consts.TYPEPAY_CREDITCARD_PAY,
    consts.TYPEPAY_BANKTRANSFERENCE,
    consts.TYPE_SOCIO,
    consts.TYPE_POLIZACREDITO,
  ];

  readonly methodConfig = {
    label: 'Tipo de pago',
    placeholder: 'Seleccionar tipo de pago…',
    required: true,
    options: this.methodOptions.map(method => ({ id: method, display: method })),
    strict: true,
    onChange: () => this.onTypePayChange(),
  };

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  savingMessage = '';
  submitted = false;
  saveError = '';
  changed = false;

  deposits: InvestmentAssetDeposit[] = [];

  options: Record<InvestmentAssetPayKind, InvestmentAssetOption[]> = {
    bankAccounts: [],
    paymentCards: [],
    cashRegisters: [],
    partners: [],
    creditPolicies: [],
  };
  loadingOptions: Partial<Record<InvestmentAssetPayKind, boolean>> = {};

  configs: Record<InvestmentAssetPayKind, any> = {
    bankAccounts: { label: 'Cuenta bancaria', placeholder: 'Seleccionar cuenta…', required: true, autoSelectSingle: true, strict: true, options: [] },
    paymentCards: { label: 'Tarjeta de crédito', placeholder: 'Seleccionar tarjeta…', required: true, autoSelectSingle: true, strict: true, options: [] },
    cashRegisters: { label: 'Caja / Efectivo', placeholder: 'Seleccionar caja…', required: true, autoSelectSingle: true, strict: true, options: [] },
    partners: { label: 'Socio', placeholder: 'Seleccionar socio…', required: true, autoSelectSingle: true, strict: true, options: [] },
    creditPolicies: { label: 'Póliza de crédito', placeholder: 'Seleccionar póliza…', required: true, autoSelectSingle: true, strict: true, options: [] },
  };

  editing: InvestmentAssetDeposit | null = null;

  private readonly missingLabels: Record<InvestmentAssetPayKind, string> = {
    bankAccounts: 'Cuenta bancaria',
    paymentCards: 'Tarjeta de crédito',
    cashRegisters: 'Caja',
    partners: 'Socio',
    creditPolicies: 'Póliza de crédito',
  };

  private readonly emptyHints: Record<InvestmentAssetPayKind, string> = {
    bankAccounts: 'No tienes cuentas bancarias dadas de alta.',
    paymentCards: 'No tienes tarjetas de crédito dadas de alta.',
    cashRegisters: 'No tienes cajas dadas de alta.',
    partners: 'No tienes socios dados de alta.',
    creditPolicies: 'No tienes pólizas de crédito dadas de alta.',
  };

  private requestedEditDone = false;
  private readonly optionRequests = new Map<InvestmentAssetPayKind, Promise<void>>();
  private readonly pendingSelection = new Map<InvestmentAssetPayKind, { id: any; label: string }>();
  private readonly loadedKinds = new Set<InvestmentAssetPayKind>();

  static open(dialog: MatDialog, data: InvestmentDialogPayData): Observable<boolean> {
    return dialog.open<InvestmentDialogPayComponent, InvestmentDialogPayData, boolean>(InvestmentDialogPayComponent, {
      width: '680px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<InvestmentDialogPayComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: InvestmentDialogPayData,
    private investmentService: InvestmentService,
    private lookups: InvestmentAssetLookupsFacade,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      typePay: [null],
      depositDate: [new Date()],
      amount: [null],
      commissions: [null],
      bankAccount: [null],
      paymentCard: [null],
      cashRegister: [null],
      partner: [null],
      creditPolicy: [null],
    });
  }

  get investment(): InvestmentAssetPayTarget {
    return this.data.investment;
  }

  get subtitle(): string {
    return [this.investment.description, this.investment.supplier].filter(Boolean).join(' · ');
  }

  get typePayControl(): UntypedFormControl {
    return this.form.get('typePay') as UntypedFormControl;
  }

  get depositDateControl(): UntypedFormControl {
    return this.form.get('depositDate') as UntypedFormControl;
  }

  get amountControl(): UntypedFormControl {
    return this.form.get('amount') as UntypedFormControl;
  }

  get commissionsControl(): UntypedFormControl {
    return this.form.get('commissions') as UntypedFormControl;
  }

  control(kind: InvestmentAssetPayKind): UntypedFormControl {
    return this.form.get(KIND_CONTROLS[kind]) as UntypedFormControl;
  }

  selectedId(kind: InvestmentAssetPayKind): any {
    const value = this.control(kind).value;
    return value && typeof value === 'object' && value.id !== undefined && value.id !== null && value.id !== '' ? value.id : null;
  }

  get typePay(): string | null {
    const value = this.typePayControl.value;
    return value && typeof value === 'object' && value.id ? value.id : null;
  }

  get total(): number {
    return roundMoney(Number(this.investment.total) || 0);
  }

  get paidAmount(): number {
    return roundMoney(this.deposits.reduce((acc, d) => acc + (Number(d.amount) || 0), 0));
  }

  get remaining(): number {
    return roundMoney(this.total - this.paidAmount);
  }

  get isPaid(): boolean {
    return this.deposits.length > 0 && this.remaining <= 0;
  }

  get maxAmount(): number {
    return roundMoney(this.remaining + (this.editing ? Number(this.editing.amount) || 0 : 0));
  }

  get showForm(): boolean {
    return !this.isPaid || !!this.editing;
  }

  get missingTypePay(): boolean { return !this.typePay; }
  get missingDate(): boolean { return !this.depositDateControl.value; }
  get missingBankAccount(): boolean { return this.typePay === this.methods.transfer && !this.selectedId('bankAccounts'); }
  get missingCard(): boolean { return this.typePay === this.methods.creditCard && !this.selectedId('paymentCards'); }
  get missingCash(): boolean { return this.typePay === this.methods.cash && !this.selectedId('cashRegisters'); }
  get missingPartner(): boolean { return this.typePay === this.methods.partner && !this.selectedId('partners'); }
  get missingCreditPolicy(): boolean { return this.typePay === this.methods.creditPolicy && !this.selectedId('creditPolicies'); }

  get invalidAmount(): boolean {
    const value = Number(this.amountControl.value);
    return !value || value <= 0 || value > this.maxAmount + 0.001;
  }

  get invalidCommissions(): boolean {
    const commissions = this.commissionsControl.value;
    return this.typePay === this.methods.transfer && commissions !== null && String(commissions) !== ''
      && (isNaN(Number(commissions)) || Number(commissions) < 0);
  }

  get amountError(): string {
    const value = Number(this.amountControl.value);
    if (!value || value <= 0) return 'Debe ser mayor que cero';
    return `No puedes pagar más de lo pendiente (${Utils.FormatMoney(this.maxAmount)} €)`;
  }

  get hasErrors(): boolean {
    return this.missingTypePay || this.missingDate || this.missingBankAccount || this.missingCard || this.missingCash
      || this.missingPartner || this.missingCreditPolicy || this.invalidAmount || this.invalidCommissions;
  }

  ngOnInit(): void {
    this.load();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  assignRemaining(): void {
    this.amountControl.setValue(this.maxAmount > 0 ? this.maxAmount : null);
  }

  onTypePayChange(): void {
    const kind = this.lookupKind(this.typePay);
    if (kind) this.ensureOptions(kind);
  }

  startEdit(deposit: InvestmentAssetDeposit): void {
    if (this.saving) return;
    this.editing = deposit;
    this.submitted = false;
    this.saveError = '';
    this.pendingSelection.clear();

    const type = this.methodOptions.indexOf(deposit.typePay) !== -1 ? deposit.typePay : null;
    this.form.patchValue({
      typePay: type ? { id: type, display: type } : null,
      depositDate: deposit.depositDate ? new Date(deposit.depositDate) : null,
      amount: Number(deposit.amount) || null,
      commissions: Number(deposit.commissions) || null,
      bankAccount: null,
      paymentCard: null,
      cashRegister: null,
      partner: null,
      creditPolicy: null,
    }, { emitEvent: false });

    const info = deposit.bankAccountInfo;
    this.rememberSelection('bankAccounts', this.guidOrNull(deposit.bankAccountsUid), info);
    this.rememberSelection('paymentCards', this.guidOrNull(deposit.paymentCardsUid), info);
    this.rememberSelection('cashRegisters', deposit.efectivoId || null, info);
    this.rememberSelection('partners', deposit.partnersId || null, info);
    this.rememberSelection('creditPolicies', deposit.polizaCreditoId || null, info);

    this.onTypePayChange();
    this.applyPendingSelections();
  }

  cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.hasErrors) return;

    const editing = !!this.editing;
    const payload = this.buildPayload();
    const request = editing
      ? this.investmentService.investmentDepositUpdate(payload)
      : this.investmentService.investmentDepositCreate(payload);

    this.saving = true;
    this.savingMessage = editing ? 'Actualizando pago…' : 'Registrando pago…';
    this.dialogRef.disableClose = true;

    request
      .then(ok => {
        if (ok !== true) throw new Error();
        this.changed = true;
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.ok(editing ? 'Pago actualizado correctamente' : 'Pago registrado correctamente');
        if (!editing || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.reloadDeposits();
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = (error && error.error && error.error.message)
          || (editing ? 'Ha ocurrido un error inesperado al actualizar el pago' : 'Ha ocurrido un error inesperado al añadir el pago');
      });
  }

  deleteDeposit(deposit: InvestmentAssetDeposit): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar pago',
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará el pago y su asiento contable, y la inversión volverá a quedar pendiente de pago. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente.',
      tone: 'danger',
      details: this.depositDetails(deposit),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando pago…',
      errorText: 'Ha ocurrido un error al eliminar el pago',
      action: () => this.investmentService.investmentDepositDelete(deposit.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Pago eliminado correctamente');
      if (this.editing && this.editing.uid === deposit.uid) this.cancelEdit();
      this.reloadDeposits();
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  trackByUid(_: number, deposit: InvestmentAssetDeposit): number {
    return deposit.uid;
  }

  private load(): void {
    this.ready = false;
    this.investmentService.investmentDepositList(this.investment.uid)
      .then(list => {
        this.deposits = (Array.isArray(list) ? list : []) as unknown as InvestmentAssetDeposit[];
        this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private reloadDeposits(): void {
    this.investmentService.investmentDepositList(this.investment.uid)
      .then(list => {
        this.deposits = (Array.isArray(list) ? list : []) as unknown as InvestmentAssetDeposit[];
        if (!this.editing) this.resetForm();
      })
      .catch(() => this.notify.error('No se pudieron recargar los pagos'));
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const row = this.deposits.find(item => item.uid === this.data.editUid);
    if (row) this.startEdit(row);
  }

  private lookupKind(typePay: string | null): InvestmentAssetPayKind | null {
    switch (typePay) {
      case this.methods.transfer: return 'bankAccounts';
      case this.methods.creditCard: return 'paymentCards';
      case this.methods.cash: return 'cashRegisters';
      case this.methods.partner: return 'partners';
      case this.methods.creditPolicy: return 'creditPolicies';
      default: return null;
    }
  }

  private ensureOptions(kind: InvestmentAssetPayKind): void {
    if (this.optionRequests.has(kind)) return;
    this.loadingOptions[kind] = true;
    this.configs[kind] = { ...this.configs[kind], hint: 'Cargando…' };
    const request = this.lookups.payOptions(kind)
      .then(list => {
        this.options[kind] = list || [];
        this.loadedKinds.add(kind);
        this.configs[kind] = {
          ...this.configs[kind],
          options: this.options[kind],
          hint: this.options[kind].length ? undefined : this.emptyHints[kind],
        };
        this.applyPendingSelections();
      })
      .catch(() => {
        this.optionRequests.delete(kind);
        this.configs[kind] = { ...this.configs[kind], hint: undefined };
        this.notify.error('No se pudieron cargar las opciones del tipo de pago');
      })
      .then(() => { this.loadingOptions[kind] = false; });
    this.optionRequests.set(kind, request);
  }

  private rememberSelection(kind: InvestmentAssetPayKind, id: any, label: string): void {
    if (id === null || id === undefined || id === '') return;
    this.pendingSelection.set(kind, { id, label: label || '' });
  }

  private applyPendingSelections(): void {
    this.pendingSelection.forEach((pending, kind) => {
      if (!this.loadedKinds.has(kind)) return;
      const option = this.findOption(this.options[kind], pending.id) || this.keepMissingOption(kind, pending);
      this.control(kind).setValue(option, { emitEvent: false });
      this.pendingSelection.delete(kind);
    });
  }

  private keepMissingOption(kind: InvestmentAssetPayKind, pending: { id: any; label: string }): any {
    const text = String(pending.label || '').trim() || this.missingLabels[kind];
    const option = { id: pending.id, display: text + ' (no disponible)', disabled: true };
    this.options[kind] = [...this.options[kind], option];
    this.configs[kind] = { ...this.configs[kind], options: this.options[kind] };
    return option;
  }

  private findOption(options: { id: any }[], id: any): any {
    const key = String(id);
    return options.find(option => String(option.id) === key)
      || options.find(option => String(option.id).toLowerCase() === key.toLowerCase())
      || null;
  }

  private buildPayload(): InvestmentsDepositsModel {
    const type = this.typePay;
    const payload = new InvestmentsDepositsModel();
    payload.uid = this.editing ? this.editing.uid : 0;
    payload.investmentsUid = this.investment.uid;
    payload.typePay = type as string;
    payload.depositDate = formatDate(this.depositDateControl.value as Date, 'yyyy-MM-dd', 'es-ES') as any;
    payload.amount = roundMoney(Number(this.amountControl.value));
    payload.commissions = type === this.methods.transfer ? roundMoney(Number(this.commissionsControl.value) || 0) : 0;
    payload.bankAccountsUid = type === this.methods.transfer && this.selectedId('bankAccounts') ? this.selectedId('bankAccounts') : consts.EMPTY_GUID;
    payload.paymentCardsUid = type === this.methods.creditCard && this.selectedId('paymentCards') ? this.selectedId('paymentCards') : consts.EMPTY_GUID;
    payload.efectivoId = type === this.methods.cash ? this.selectedId('cashRegisters') : null as any;
    payload.partnersId = type === this.methods.partner ? Number(this.selectedId('partners')) || 0 : 0;
    payload.polizaCreditoId = type === this.methods.creditPolicy ? this.selectedId('creditPolicies') : null as any;
    return payload;
  }

  private resetForm(): void {
    this.submitted = false;
    this.saveError = '';
    this.pendingSelection.clear();
    this.form.reset({
      typePay: null,
      depositDate: new Date(),
      amount: this.remaining > 0 ? this.remaining : null,
      commissions: null,
      bankAccount: null,
      paymentCard: null,
      cashRegister: null,
      partner: null,
      creditPolicy: null,
    }, { emitEvent: false });
  }

  private guidOrNull(value: string): string | null {
    return value && value !== consts.EMPTY_GUID ? value : null;
  }

  private depositDetails(deposit: InvestmentAssetDeposit): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [
      { label: 'Importe', value: Utils.FormatMoney(Number(deposit.amount) || 0) + ' €' },
    ];
    if (deposit.depositDate) details.push({ label: 'Fecha', value: formatDate(deposit.depositDate, 'dd/MM/yyyy', 'es-ES') });
    if (deposit.typePay) details.push({ label: 'Forma de pago', value: deposit.typePay + (deposit.bankAccountInfo ? ' · ' + deposit.bankAccountInfo : '') });
    return details;
  }
}
